import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { Download, RefreshCw } from "lucide-react";
import { invalidateDashboardOverview } from "../../services/dashboardApi";
import {
  parsePayrollProcessError,
  fetchPayrollRunDetail,
  fetchPayrollSalaryPackageCheck,
  getPayroll,
  getSalaryStatement,
  listPayrollProcessApprovals,
  newIdempotencyKey,
  processPayroll,
  rejectPayrollProcessApproval,
  requestPayrollProcessApproval,
  resetPayrollForReprocess,
} from "./payrollApi";
import { formatMoney, resolveRunStatus } from "./payrollRunUtils";
import { downloadPayrollFile } from "./payrollProcessExport";
import { fetchPayPeriodAttendanceSummary } from "../attendance/attendanceApi";
import PayrollRunSelect from "../../components/common/PayrollRunSelect";
import { usePayrollWorkflow, usePrefilledPayrollRunId } from "./payrollWorkflow";
import { useEmployees } from "../../hooks/useEmployees";
import NoticeCard from "../../components/ui/NoticeCard";
import API from "../../services/api";
import { AuthContext } from "../Context/AuthContext";
import { isAdminLike } from "../../rbac/access";

const PAGE_SIZE = 5;

function fmtIsoDate(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString("en-IN", { dateStyle: "medium" });
}

function salaryAssignPath(missingRows) {
  const first = Array.isArray(missingRows) ? missingRows[0] : null;
  const id = first?.employee_id;
  return id
    ? `/employeesalarystructure?employee_id=${encodeURIComponent(id)}`
    : "/employeesalarystructure";
}

function buildSalaryPackageNotice(missingRows, periodEnd) {
  const missing = Array.isArray(missingRows) ? missingRows : [];
  if (!missing.length) return null;

  const futureRows = missing.filter((row) => row.reason === "future_effective");
  const noPackageRows = missing.filter((row) => row.reason !== "future_effective");
  const periodLabel = periodEnd ? fmtIsoDate(periodEnd) : "this pay period";
  const assignTo = salaryAssignPath(missing);

  if (futureRows.length === missing.length) {
    const names = futureRows
      .slice(0, 4)
      .map((row) => row.employee_name || row.employee_code || row.employee_id)
      .join(", ");
    const suffix = futureRows.length > 4 ? ` (+${futureRows.length - 4} more)` : "";
    return {
      title: "Salary package effective date is too late",
      message: `${futureRows.length} employee${futureRows.length === 1 ? "" : "s"} ha${futureRows.length === 1 ? "s" : "ve"} a salary package, but it starts after ${periodLabel}.`,
      hint: names ? `Affected: ${names}${suffix}` : null,
      steps: [
        "Open Employee Salary Structure",
        `Set Effective From on or before ${periodLabel}`,
        "Return here and click Process Payroll",
      ],
      assignTo,
    };
  }

  const names = missing
    .slice(0, 4)
    .map((row) => row.employee_name || row.employee_code || row.employee_id)
    .join(", ");
  const suffix = missing.length > 4 ? ` (+${missing.length - 4} more)` : "";
  const noPkgNames = noPackageRows
    .slice(0, 4)
    .map((row) => row.employee_name || row.employee_code || row.employee_id)
    .join(", ");
  return {
    title: "Salary packages required before processing",
    message: `${missing.length} active employee${missing.length === 1 ? "" : "s"} still need a valid salary template and CTC for ${periodLabel}.`,
    hint: noPackageRows.length
      ? `${noPkgNames || names}${suffix} ${noPackageRows.length === 1 ? "has" : "have"} no salary package yet — changing dates on other employees will not clear this error.`
      : names
        ? `Affected: ${names}${suffix}`
        : null,
    steps: [
      "Open Employee Salary Structure",
      `Select ${noPkgNames || names || "each listed employee"} and assign a template + annual CTC`,
      `Set Effective From on or before ${periodLabel}`,
      "Return here and click Process Payroll",
    ],
    assignTo,
  };
}

function payrollNoticeActions(errorDetail, { payrollId, regenerateLoading, onReset, onDismiss }) {
  if (!errorDetail) return null;

  const btnBase =
    "inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50";

  // Guided page buttons are rendered via NoticeCard guideLinks — keep only special actions here.
  if (errorDetail.actionType === "reset") {
    return (
      <>
        <button
          type="button"
          onClick={onReset}
          disabled={regenerateLoading || !payrollId?.trim()}
          className={`${btnBase} bg-amber-600 text-white hover:bg-amber-700`}
        >
          {regenerateLoading ? "Recalculating..." : "Refresh & recalculate"}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className={`${btnBase} border border-slate-300 bg-white text-slate-700 hover:bg-slate-50`}
        >
          Dismiss
        </button>
      </>
    );
  }

  if (errorDetail.actionType === "salary_packages" && !(errorDetail.guideLinks?.length > 0)) {
    const assignTo =
      errorDetail.guideLinks?.[0]?.to ||
      errorDetail.assignTo ||
      "/employeesalarystructure";
    return (
      <>
        <Link
          to={assignTo}
          className={`${btnBase} bg-indigo-600 text-white hover:bg-indigo-700`}
        >
          Assign salary packages
        </Link>
        <button type="button" onClick={onDismiss} className={`${btnBase} border border-slate-300 bg-white text-slate-700 hover:bg-slate-50`}>
          Dismiss
        </button>
      </>
    );
  }

  if (errorDetail.actionType === "derived_variables" && !(errorDetail.guideLinks?.length > 0)) {
    return (
      <>
        <Link
          to="/salary-v2/derived-variables"
          className={`${btnBase} bg-indigo-600 text-white hover:bg-indigo-700`}
        >
          Add missing formula
        </Link>
        <Link
          to="/salary/templates"
          className={`${btnBase} border border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-50`}
        >
          Review salary templates
        </Link>
        <button type="button" onClick={onDismiss} className={`${btnBase} border border-slate-300 bg-white text-slate-700 hover:bg-slate-50`}>
          Dismiss
        </button>
      </>
    );
  }

  if (errorDetail.actionType === "salary_templates" && !(errorDetail.guideLinks?.length > 0)) {
    return (
      <>
        <Link
          to="/salary/templates"
          className={`${btnBase} bg-indigo-600 text-white hover:bg-indigo-700`}
        >
          Review salary templates
        </Link>
        <Link
          to="/salary-v2/derived-variables"
          className={`${btnBase} border border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-50`}
        >
          Open derived variables
        </Link>
        <button type="button" onClick={onDismiss} className={`${btnBase} border border-slate-300 bg-white text-slate-700 hover:bg-slate-50`}>
          Dismiss
        </button>
      </>
    );
  }

  if (errorDetail.actionType === "salary_setup" && !(errorDetail.guideLinks?.length > 0)) {
    return (
      <>
        <Link
          to="/salary-v2/groups"
          className={`${btnBase} bg-indigo-600 text-white hover:bg-indigo-700`}
        >
          Open salary component groups
        </Link>
        <Link
          to="/salary/templates"
          className={`${btnBase} border border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-50`}
        >
          Review salary templates
        </Link>
        <button type="button" onClick={onDismiss} className={`${btnBase} border border-slate-300 bg-white text-slate-700 hover:bg-slate-50`}>
          Dismiss
        </button>
      </>
    );
  }

  if (errorDetail.actionType === "attendance" && !(errorDetail.guideLinks?.length > 0)) {
    return (
      <>
        <Link to="/attendance" className={`${btnBase} bg-indigo-600 text-white hover:bg-indigo-700`}>
          Open attendance
        </Link>
        <button type="button" onClick={onDismiss} className={`${btnBase} border border-slate-300 bg-white text-slate-700 hover:bg-slate-50`}>
          Dismiss
        </button>
      </>
    );
  }

  return (
    <button type="button" onClick={onDismiss} className={`${btnBase} border border-slate-300 bg-white text-slate-700 hover:bg-slate-50`}>
      Dismiss
    </button>
  );
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function fmtNum(v, digits = 2) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString("en-IN", { maximumFractionDigits: digits }) : "—";
}

function numOrZero(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function monthlySalaryFromAnnual(annualCtc) {
  const annual = Number(annualCtc);
  if (!Number.isFinite(annual) || annual <= 0) return null;
  return annual / 12;
}

function payableMonthlyAmount(row) {
  const monthly = numOrZero(row.monthly_salary);
  if (monthly <= 0) return null;
  const working = numOrZero(row.total_working_days);
  const payable = numOrZero(row.attendance_payable_days ?? row.payable_days);
  if (working > 0) return (monthly * payable) / working;
  return monthly;
}

function moneyOrDash(value) {
  if (value == null || value === "") return "—";
  return `₹${formatMoney(value)}`;
}

function TablePager({ page, pageCount, total, onPage }) {
  if (total <= PAGE_SIZE) return null;
  const start = (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, total);
  const pages = [];
  for (let i = 1; i <= pageCount; i += 1) pages.push(i);
  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm">
      <p className="text-slate-500">
        Showing {start}–{end} of {total}
      </p>
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-slate-700 disabled:opacity-40 hover:bg-slate-50"
        >
          Previous
        </button>
        {pages.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onPage(n)}
            className={`min-w-8 rounded-lg border px-2.5 py-1.5 tabular-nums ${
              n === page
                ? "border-indigo-600 bg-indigo-600 text-white"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            {n}
          </button>
        ))}
        <button
          type="button"
          disabled={page >= pageCount}
          onClick={() => onPage(page + 1)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-slate-700 disabled:opacity-40 hover:bg-slate-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function DownloadMenu({ disabled, onPick }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (!boxRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-semibold text-slate-800 shadow-sm hover:border-indigo-300 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <Download size={15} />
        Download
      </button>
      {open ? (
        <div className="absolute right-0 z-20 mt-2 w-56 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg">
          <p className="px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Choose a format
          </p>
          {[
            ["xlsx", "Excel", "Spreadsheet with every pay field"],
            ["csv", "CSV", "Opens in Excel or Sheets"],
            ["pdf", "PDF", "Printable processed payroll"],
          ].map(([id, label, hint]) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                setOpen(false);
                onPick(id);
              }}
              className="flex w-full flex-col items-start rounded-lg px-2.5 py-2 text-left hover:bg-slate-50"
            >
              <span className="text-sm font-semibold text-slate-900">{label}</span>
              <span className="text-xs text-slate-500">{hint}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ConfirmDialog({ open, title, message, confirmLabel, busy, danger, onCancel, onConfirm, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 px-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">{message}</p>
        {children}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${
              danger ? "bg-rose-600 hover:bg-rose-700" : "bg-indigo-600 hover:bg-indigo-700"
            }`}
          >
            {busy ? "Please wait..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionCard({ step, title, subtitle, actions, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold uppercase tracking-wider text-indigo-600">Step {step}</p>
          <h2 className="text-base font-semibold text-slate-900 mt-0.5">{title}</h2>
          {subtitle ? <p className="text-sm text-slate-600 mt-1 leading-relaxed">{subtitle}</p> : null}
        </div>
        {actions ? <div className="shrink-0 flex flex-wrap items-center justify-end gap-2 pt-0.5">{actions}</div> : null}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function StatTile({ label, value, hint, accent }) {
  return (
    <div className={`rounded-xl border p-4 ${accent || "bg-white border-slate-200"}`}>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
      <p className="text-xl font-semibold text-slate-900 mt-1 tabular-nums">{value}</p>
      {hint ? <p className="text-xs text-slate-500 mt-1">{hint}</p> : null}
    </div>
  );
}

function isTerminal(executionStatus, runStatus) {
  if (["completed", "failed", "partial_success"].includes(executionStatus || "")) {
    return true;
  }
  if (runStatus === "processed") return true;
  return false;
}


function ApprovalFacts({ approval }) {
  const lines = [
    ["Payroll month", approval.payroll_month || "—"],
    ["Requested", fmtDateTime(approval.requested_at)],
    ["Raised by", approval.requested_by_name || "HR"],
    ["Runs for", `${fmtIsoDate(approval.period_start)} → ${fmtIsoDate(approval.period_end)}`],
    ["Employees", approval.employee_count ?? "—"],
    ["Attendance pay", moneyOrDash(approval.attendance_pay)],
  ];
  if (Number(approval.advances) > 0) lines.push(["Advances", moneyOrDash(approval.advances)]);
  if (Number(approval.overtime) > 0) lines.push(["Overtime", moneyOrDash(approval.overtime)]);
  if (Number(approval.leave_encashment) > 0) lines.push(["Leave encashment", moneyOrDash(approval.leave_encashment)]);
  if (Number(approval.fuel_allowance) > 0) lines.push(["Fuel allowance", moneyOrDash(approval.fuel_allowance)]);
  for (const item of approval.other_items || []) {
    if (Number(item.amount) > 0) lines.push([item.label || "Other", moneyOrDash(item.amount)]);
  }
  return (
    <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
      {lines.map(([label, value]) => (
        <div key={label} className="flex items-baseline justify-between gap-3 border-b border-slate-100 py-1.5">
          <dt className="text-slate-500">{label}</dt>
          <dd className="font-semibold text-slate-900 text-right tabular-nums">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function ProcessPayroll({ embedded = false }) {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { role } = useContext(AuthContext);
  const isSuperAdmin = isAdminLike(role);
  const { employees } = useEmployees();
  const { payPeriodId, payPeriodLabel } = usePayrollWorkflow();
  const [payrollId, setPayrollId] = useState("");

  const [runSnapshot, setRunSnapshot] = useState(null);
  const [, setQueueMeta] = useState({
    celery_task_id: null,
    idempotencyKey: null,
  });
  const [polling, setPolling] = useState(false);
  const [processError, setProcessError] = useState(null);
  const [actionNotice, setActionNotice] = useState(null);
  const [readyVisible, setReadyVisible] = useState(false);
  const [tablePage, setTablePage] = useState(1);
  const [salaryStatement, setSalaryStatement] = useState([]);
  const [approvalQueue, setApprovalQueue] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const [requestBusy, setRequestBusy] = useState(false);
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceError, setAttendanceError] = useState(null);
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [packageCheckLoading, setPackageCheckLoading] = useState(false);
  const [salaryPackageCheck, setSalaryPackageCheck] = useState(null);
  const [runDetail, setRunDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [fuelPending, setFuelPending] = useState(null);
  const [payrollIdNotice, setPayrollIdNotice] = useState(
    () => location.state?.payrollIdNotice || null
  );

  const pollRef = useRef(null);
  const idempotencyAttemptRef = useRef(null);
  const detailRef = useRef(null);

  usePrefilledPayrollRunId(setPayrollId);

  useEffect(() => {
    const incoming = location.state?.payrollIdNotice;
    if (incoming?.name) setPayrollIdNotice(incoming);
  }, [location.state]);

  useEffect(() => {
    const ppId = runSnapshot?.pay_period_id || payPeriodId;
    if (!ppId) {
      setFuelPending(null);
      return undefined;
    }
    let cancelled = false;
    API.get("/fuel-reimbursement/pending", { params: { pay_period_id: ppId } })
      .then((res) => {
        if (!cancelled) setFuelPending(res.data);
      })
      .catch(() => {
        if (!cancelled) setFuelPending(null);
      });
    return () => {
      cancelled = true;
    };
  }, [runSnapshot?.pay_period_id, payPeriodId, runSnapshot?.status]);

  useEffect(() => {
    const ppId = runSnapshot?.pay_period_id;
    if (!ppId) {
      setAttendanceSummary(null);
      setAttendanceError(null);
      return undefined;
    }
    let cancelled = false;
    setAttendanceLoading(true);
    setAttendanceError(null);
    fetchPayPeriodAttendanceSummary(ppId)
      .then((data) => {
        if (!cancelled) setAttendanceSummary(data);
      })
      .catch((e) => {
        if (!cancelled) {
          setAttendanceSummary(null);
          setAttendanceError(e?.message || "Could not load attendance summary");
        }
      })
      .finally(() => {
        if (!cancelled) setAttendanceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [runSnapshot?.pay_period_id]);

  const refreshSalaryPackageCheck = useCallback(async (runId) => {
    const id = String(runId || "").trim();
    if (!id) {
      setSalaryPackageCheck(null);
      return;
    }
    setPackageCheckLoading(true);
    try {
      const res = await fetchPayrollSalaryPackageCheck(id);
      setSalaryPackageCheck(res.data || null);
    } catch {
      setSalaryPackageCheck(null);
    } finally {
      setPackageCheckLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSalaryPackageCheck(payrollId);
  }, [payrollId, refreshSalaryPackageCheck]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && payrollId.trim()) {
        refreshSalaryPackageCheck(payrollId);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [payrollId, refreshSalaryPackageCheck]);

  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setPolling(false);
  };

  const refreshRun = async (id) => {
    const { data } = await getPayroll(id);
    setRunSnapshot(data);
    setDetailLoading(true);
    let detail = null;
    try {
      const detailRes = await fetchPayrollRunDetail(id);
      detail = detailRes.data;
      setRunDetail(detail);
    } catch {
      setRunDetail(null);
    } finally {
      setDetailLoading(false);
    }
    detailRef.current = detail;
    return data;
  };

  useEffect(() => {
    if (!payrollId.trim()) {
      setRunSnapshot(null);
      setRunDetail(null);
      return;
    }
    refreshRun(payrollId.trim()).catch(() => {});
  }, [payrollId]);

  const noteProcessed = (data, detail) => {
    const totals = detail?.summary?.totals || detailRef.current?.summary?.totals || {};
    const gross = data?.gross_pay_total;
    const net = data?.net_pay_total;
    const deductions = totals.deductions ?? (Number(gross) - Number(net));
    const count = data?.execution_meta?.employees_processed ?? detail?.employees?.length;
    setActionNotice({
      variant: "success",
      title: "Payroll processed successfully",
      message: `Completed on ${fmtDateTime(data?.processed_at || new Date().toISOString())} for ${data?.period_label || payPeriodLabel || "this Payroll ID"}. Gross ₹${formatMoney(gross)}, net ₹${formatMoney(net)}, deductions ₹${formatMoney(deductions)}${count != null ? `, ${count} employees` : ""}. The table below is the processed salary. Download is available in Excel, CSV, or PDF.`,
    });
  };

  const startPolling = (id) => {
    stopPolling();
    setPolling(true);
    const tick = async () => {
      try {
        const data = await refreshRun(id);
        const es = data.execution_status;
        const st = data.status;
        if (isTerminal(es, st)) {
          stopPolling();
          if (st === "processed" || es === "completed") {
            invalidateDashboardOverview(queryClient);
            noteProcessed(data, null);
          }
        }
      } catch (e) {
        setProcessError(parsePayrollProcessError({ message: e?.response?.data?.detail || e.message || "Poll failed" }));
        stopPolling();
      }
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
  };

  const runPayroll = async () => {
    if (!payrollId.trim()) {
      setProcessError({ title: "Select a payroll run", message: "Please choose a payroll run before processing." });
      return;
    }

    const id = payrollId.trim();
    setProcessError(null);
    setConfirmState(null);

    const key = idempotencyAttemptRef.current || newIdempotencyKey();
    idempotencyAttemptRef.current = key;

    try {
      const res = await processPayroll(id, { idempotencyKey: key });

      setQueueMeta({
        celery_task_id: res.data?.celery_task_id || null,
        idempotencyKey: res.idempotencyKey,
      });

      if (res.status === 202) {
        await refreshRun(id);
        startPolling(id);
        idempotencyAttemptRef.current = null;
        setActionNotice({
          variant: "info",
          title: "Payroll is processing",
          message: `Started on ${fmtDateTime(new Date().toISOString())}. This page updates every few seconds until the salary calculation finishes.`,
        });
        return;
      }

      if (res.status === 200) {
        const data = await refreshRun(id);
        stopPolling();
        invalidateDashboardOverview(queryClient);
        idempotencyAttemptRef.current = null;
        await refreshSalaryPackageCheck(id);
        noteProcessed(data, null);
      }
    } catch (e) {
      setProcessError(parsePayrollProcessError(e));
    }
  };

  const regenerateForAttendanceMode = async () => {
    if (!payrollId.trim()) {
      setProcessError({ title: "Select a payroll run", message: "Please choose a payroll run before refreshing." });
      return;
    }
    const id = payrollId.trim();
    setRegenerateLoading(true);
    setProcessError(null);
    try {
      await resetPayrollForReprocess(id);
      idempotencyAttemptRef.current = null;
      await refreshRun(id);
      const when = fmtDateTime(new Date().toISOString());
      setActionNotice({
        variant: "success",
        title: "Refreshed and recalculated",
        message: `Attendance and payable days were recalculated on ${when}. This payroll is draft again. Review the attendance table, then process it when the days look right.`,
      });
    } catch (e) {
      setProcessError(parsePayrollProcessError(e));
    } finally {
      setRegenerateLoading(false);
    }
  };

  const startedAt = runSnapshot?.created_at;
  const completedAt = runSnapshot?.processed_at;

  const processed = (runSnapshot?.status || "").toLowerCase() === "processed";
  const employeesWithoutPackage = salaryPackageCheck?.employees_without_package || [];
  const payrollBlockedMissingPackages = Boolean(
    payrollId.trim() && salaryPackageCheck && salaryPackageCheck.ready === false
  );
  const salaryPackageNotice = buildSalaryPackageNotice(
    employeesWithoutPackage,
    salaryPackageCheck?.period_end || runSnapshot?.period_end
  );

  const payrollMode =
    runSnapshot?.execution_meta?.input_snapshot?.payroll_settings?.payable_days_mode
    || runSnapshot?.execution_meta?.payroll_settings_at_creation?.payable_days_mode
    || "calendar";

  const employeeRows = useMemo(() => {
    const snapDbg = runSnapshot?.execution_meta?.input_snapshot?.attendance_debug_by_employee || {};
    const attById = {};
    for (const row of attendanceSummary?.employees || []) {
      attById[row.employee_id] = row;
    }
    const annualById = new Map(
      (employees || []).map((emp) => [String(emp.employee_id), emp.annual_ctc])
    );
    const monthlyFor = (employeeId) => monthlySalaryFromAnnual(annualById.get(String(employeeId)));

    if (runDetail?.employees?.length) {
      return runDetail.employees.map((emp) => {
        const att = attById[emp.employee_id] || {};
        const dbg = snapDbg[emp.employee_id] || {};
        return {
          ...emp,
          total_working_days: emp.total_working_days ?? att.total_working_days ?? dbg.TOTAL_WORKING_DAYS ?? dbg.total_working_days,
          payable_days: emp.payable_days ?? att.payable_days ?? dbg.PAYABLE_DAYS ?? dbg.payable_days,
          worked_days: emp.worked_days ?? att.worked_days ?? dbg.WORKED_DAYS ?? dbg.worked_days,
          lop_units: emp.lop_units ?? att.lop_units ?? dbg.LOP_UNITS ?? dbg.lop_units,
          wage_proration_factor: emp.wage_proration_factor ?? att.wage_proration_factor ?? dbg.WAGE_PRORATION_FACTOR ?? dbg.wage_proration_factor,
          present_units: att.present_units ?? emp.present_units ?? dbg.present_units,
          absent_units: att.absent_units ?? emp.absent_units ?? dbg.absent_units,
          half_day_units: att.half_day_units ?? emp.half_day_units ?? dbg.half_day_units ?? dbg.HALF_DAY_UNITS,
          paid_leave_units: att.paid_leave_units ?? emp.paid_leave_units ?? dbg.paid_leave_units ?? dbg.PAID_LEAVE_UNITS,
          unpaid_leave_units: att.unpaid_leave_units ?? emp.unpaid_leave_units ?? dbg.unpaid_leave_units ?? dbg.UNPAID_LEAVE_UNITS,
          holiday_units: att.holiday_units ?? dbg.holiday_units ?? dbg.HOLIDAY_UNITS ?? emp.holiday_units,
          attendance_payable_days: att.payable_days ?? emp.payable_days ?? dbg.PAYABLE_DAYS ?? dbg.payable_days,
          attendance_factor: att.wage_proration_factor ?? emp.wage_proration_factor ?? dbg.WAGE_PRORATION_FACTOR ?? dbg.wage_proration_factor,
          monthly_salary: att.monthly_salary ?? monthlyFor(emp.employee_id),
          half_day_detail: att.half_day_detail || [],
          leave_detail: att.leave_detail || [],
          absent_detail: att.absent_detail || [],
          hasPayroll: Number(emp.earnings) > 0 || Number(emp.deductions) > 0 || Number(emp.net_salary) > 0,
        };
      });
    }

    return (attendanceSummary?.employees || []).map((row) => {
      const dbg = snapDbg[row.employee_id] || {};
      return {
        employee_id: row.employee_id,
        employee_name: row.employee_name || row.employee_id,
        employee_code: row.employee_code,
        present_units: row.present_units ?? dbg.present_units,
        absent_units: row.absent_units ?? dbg.absent_units,
        half_day_units: row.half_day_units ?? dbg.half_day_units ?? dbg.HALF_DAY_UNITS,
        paid_leave_units: row.paid_leave_units ?? dbg.paid_leave_units ?? dbg.PAID_LEAVE_UNITS,
        unpaid_leave_units: row.unpaid_leave_units ?? dbg.unpaid_leave_units ?? dbg.UNPAID_LEAVE_UNITS,
        holiday_units: row.holiday_units ?? dbg.holiday_units ?? dbg.HOLIDAY_UNITS,
        attendance_payable_days: row.payable_days ?? dbg.PAYABLE_DAYS ?? dbg.payable_days,
        attendance_factor: row.wage_proration_factor ?? dbg.WAGE_PRORATION_FACTOR ?? dbg.wage_proration_factor,
        monthly_salary: row.monthly_salary ?? monthlyFor(row.employee_id),
        half_day_detail: row.half_day_detail || [],
        leave_detail: row.leave_detail || [],
        absent_detail: row.absent_detail || [],
        total_working_days: row.total_working_days ?? dbg.TOTAL_WORKING_DAYS ?? dbg.total_working_days,
        payable_days: row.payable_days ?? dbg.PAYABLE_DAYS ?? dbg.payable_days,
        worked_days: row.worked_days ?? dbg.WORKED_DAYS ?? dbg.worked_days,
        lop_units: row.lop_units ?? dbg.LOP_UNITS ?? dbg.lop_units,
        wage_proration_factor: row.wage_proration_factor ?? dbg.WAGE_PRORATION_FACTOR ?? dbg.wage_proration_factor,
        earnings: null,
        deductions: null,
        net_salary: null,
        hasPayroll: false,
      };
    });
  }, [runDetail, attendanceSummary, runSnapshot, employees]);

  const runTotals = runDetail?.summary?.totals || {};
  const approval = runSnapshot?.execution_meta?.process_approval || null;
  const approvalPending = approval?.status === "pending" && !processed;
  const approvalRejected = approval?.status === "rejected" && !processed;
  const periodName = payPeriodLabel || runSnapshot?.period_label || "this Payroll ID";
  const liveRunStatus = resolveRunStatus({
    ...(runSnapshot || {}),
    status: processed ? "processed" : runSnapshot?.status,
    execution_status: processed ? (runSnapshot?.execution_status || "completed") : runSnapshot?.execution_status,
  });
  const pageCount = Math.max(1, Math.ceil(employeeRows.length / PAGE_SIZE));
  const safePage = Math.min(tablePage, pageCount);
  const pageRows = employeeRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const attendancePay = employeeRows.reduce((sum, row) => sum + (payableMonthlyAmount(row) || 0), 0);

  useEffect(() => {
    setTablePage(1);
  }, [payrollId, processed]);

  useEffect(() => {
    const show = !processError && !processed && !actionNotice && (runSnapshot?.execution_meta?.reprocess_history?.length > 0);
    if (!show) {
      setReadyVisible(false);
      return undefined;
    }
    setReadyVisible(true);
    const timer = setTimeout(() => setReadyVisible(false), 30000);
    return () => clearTimeout(timer);
  }, [processError, processed, actionNotice, payrollId, runSnapshot?.execution_meta?.reprocess_history?.length]);

  useEffect(() => {
    if (!processed || !payrollId.trim()) {
      setSalaryStatement([]);
      return undefined;
    }
    let cancelled = false;
    getSalaryStatement(payrollId.trim())
      .then((res) => {
        const payload = res.data?.salary_statement || res.data;
        if (!cancelled) setSalaryStatement(Array.isArray(payload) ? payload : []);
      })
      .catch(() => {
        if (!cancelled) setSalaryStatement([]);
      });
    return () => {
      cancelled = true;
    };
  }, [processed, payrollId, runSnapshot?.processed_at]);

  const loadApprovalQueue = useCallback(async () => {
    if (!isSuperAdmin) {
      setApprovalQueue([]);
      return;
    }
    try {
      const res = await listPayrollProcessApprovals();
      setApprovalQueue(res.data?.items || []);
    } catch {
      setApprovalQueue([]);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    loadApprovalQueue();
  }, [loadApprovalQueue, payrollId, runSnapshot?.execution_meta?.process_approval?.status, processed]);

  const otherPending = approvalQueue.filter((item) => String(item.payroll_run_id) !== String(payrollId));

  const askProcess = () => {
    if (!payrollId.trim()) {
      setProcessError({ title: "Select a Payroll ID", message: "Choose a Payroll ID before processing." });
      return;
    }
    setConfirmState({
      title: approvalPending ? "Approve and process this payroll?" : "Process this payroll?",
      message: approvalPending
        ? `${periodName} was requested by ${approval?.requested_by_name || "HR"} on ${fmtDateTime(approval?.requested_at)}. Approving calculates salary for ${employeeRows.length} employees and saves it.`
        : `${periodName} will be calculated for ${employeeRows.length} employees and saved as processed.`,
      confirmLabel: approvalPending ? "Approve & process" : "Process payroll",
      action: "process",
    });
  };

  const askApprovalRequest = () => {
    if (!payrollId.trim()) {
      setProcessError({ title: "Select a Payroll ID", message: "Choose a Payroll ID before requesting approval." });
      return;
    }
    setConfirmState({
      title: "Send this payroll for approval?",
      message: "Salary is not processed until a super admin approves it. The request includes the month, when you raised it, and the amounts below.",
      confirmLabel: "Send request",
      action: "request",
    });
  };

  const submitApprovalRequest = async () => {
    const id = payrollId.trim();
    setRequestBusy(true);
    setProcessError(null);
    try {
      await requestPayrollProcessApproval(id, {
        employee_count: employeeRows.length,
        attendance_pay: attendancePay,
      });
      await refreshRun(id);
      const when = fmtDateTime(new Date().toISOString());
      setActionNotice({
        variant: "success",
        title: "Approval request sent",
        message: `Sent to the super admin on ${when} for ${payPeriodLabel || approval?.payroll_month || "this payroll"}. It appears under Approvals → Payroll as a payroll proceed request until they approve it.`,
      });
      setConfirmState(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setProcessError({
        title: "Could not send the request",
        message: typeof detail === "string" ? detail : "Try again in a moment.",
      });
      setConfirmState(null);
    } finally {
      setRequestBusy(false);
    }
  };

  const rejectApproval = async () => {
    const id = payrollId.trim();
    setRequestBusy(true);
    setProcessError(null);
    try {
      await rejectPayrollProcessApproval(id, "Rejected by super admin");
      await refreshRun(id);
      setActionNotice({
        variant: "warning",
        title: "Payroll request rejected",
        message: `Rejected on ${fmtDateTime(new Date().toISOString())}. Salary was not processed. HR can send a new request after reviewing the payroll.`,
      });
      setConfirmState(null);
      loadApprovalQueue();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setProcessError({
        title: "Could not reject the request",
        message: typeof detail === "string" ? detail : "Try again in a moment.",
      });
      setConfirmState(null);
    } finally {
      setRequestBusy(false);
    }
  };

  const onDownload = (format) => {
    downloadPayrollFile(format, {
      employeeRows,
      statement: salaryStatement,
      periodLabel: payPeriodLabel,
      processedAt: fmtDateTime(completedAt),
    });
  };

  return (
    <div className={embedded ? "pp-hub-embedded" : "bw-page bg-gray-50 min-h-screen"}>
      {!embedded ? (
        <header className="bw-page-header">
          <div className="relative z-[1] flex flex-wrap justify-between items-start gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="bw-page-title">Process Payroll</h1>
              <p className="bw-page-subtitle max-w-2xl">
                Calculate monthly salary for each employee using attendance, payable days, and assigned salary packages.
              </p>
            </div>
          </div>
        </header>
      ) : null}

      <div className={embedded ? "space-y-5" : "max-w-6xl mx-auto space-y-5"}>
        {payrollIdNotice?.name ? (
          <NoticeCard
            variant="success"
            badgeLabel="Created"
            title={
              payrollIdNotice.kind === "created"
                ? "Payroll ID created successfully"
                : "Payroll ID is ready"
            }
            message={
              payrollIdNotice.kind === "created"
                ? `${payrollIdNotice.name} was created. You are on the pay run page for this Payroll ID.`
                : `${payrollIdNotice.name} is already created, so this pay run page opened directly.`
            }
            hint="The Payroll ID name on this page is the same date range you created."
            onDismiss={() => setPayrollIdNotice(null)}
          />
        ) : null}
        {processError && (
          <NoticeCard
            variant={processError.variant || "error"}
            title={processError.title}
            message={processError.message}
            hint={processError.hint}
            steps={processError.steps}
            stepsTitle="What to do next — follow these setup pages"
            guideLinks={processError.guideLinks}
            onDismiss={() => setProcessError(null)}
            actions={payrollNoticeActions(processError, {
              payrollId,
              regenerateLoading,
              onReset: async () => {
                setProcessError(null);
                await regenerateForAttendanceMode();
              },
              onDismiss: () => setProcessError(null),
            })}
          />
        )}

        {!packageCheckLoading && payrollBlockedMissingPackages && !processError && salaryPackageNotice && (
          <NoticeCard
            variant="error"
            title={salaryPackageNotice.title}
            message={salaryPackageNotice.message}
            hint={salaryPackageNotice.hint || "Payroll cannot calculate amounts until every active employee has a package valid for this pay period."}
            steps={salaryPackageNotice.steps}
            stepsTitle="What to do next — follow these setup pages"
            guideLinks={[
              {
                label: "Assign to Employees",
                to: salaryPackageNotice.assignTo || "/employeesalarystructure",
                primary: true,
              },
              { label: "3.3 Salary Structures", to: "/salary-v2/structures" },
              { label: "3.2 Salary Components", to: "/salary-v2/components" },
            ]}
          />
        )}

        {!packageCheckLoading && !payrollBlockedMissingPackages && !processError && (salaryPackageCheck?.auto_assigned || []).length > 0 && (
          <NoticeCard
            variant="info"
            title="Default template assigned automatically"
            message={`${salaryPackageCheck.auto_assigned.length} employee${salaryPackageCheck.auto_assigned.length === 1 ? "" : "s"} had no salary package. “${salaryPackageCheck.default_template_name || "The organisation default"}” was assigned so payroll can run without errors.`}
            hint={
              salaryPackageCheck.auto_assigned
                .slice(0, 4)
                .map((row) => row.employee_name || row.employee_code)
                .filter(Boolean)
                .join(", ")
              + (salaryPackageCheck.auto_assigned.length > 4 ? ` (+${salaryPackageCheck.auto_assigned.length - 4} more)` : "")
            }
          />
        )}

        {readyVisible ? (
          <NoticeCard
            variant="info"
            title="Ready to process"
            message="This payroll was refreshed and is ready for a new calculation. This note closes in 30 seconds."
            steps={[
              "Review attendance in the table below",
              isSuperAdmin ? "Click Process Payroll and confirm" : "Send it to the super admin for approval",
            ]}
          />
        ) : null}

        {actionNotice ? (
          <NoticeCard
            variant={actionNotice.variant || "success"}
            title={actionNotice.title}
            message={actionNotice.message}
            onDismiss={() => setActionNotice(null)}
          />
        ) : null}

        {!actionNotice && processed ? (
          <NoticeCard
            variant="success"
            title="Payroll is processed"
            message={`Completed on ${fmtDateTime(completedAt)} for ${periodName}. Gross ₹${formatMoney(runSnapshot?.gross_pay_total)}, net ₹${formatMoney(runSnapshot?.net_pay_total)}, deductions ₹${formatMoney(runTotals.deductions)}, ${runSnapshot?.execution_meta?.employees_processed ?? employeeRows.length} employees. Download is available above the table.`}
          />
        ) : null}

        {fuelPending?.enabled && Number(fuelPending.pending_count) > 0 ? (
          <NoticeCard
            variant="warning"
            title="Pending fuel bills"
            message={`${fuelPending.pending_count} pending fuel bill${Number(fuelPending.pending_count) === 1 ? "" : "s"} (${formatMoney(fuelPending.pending_amount)}) are not included in this payroll.`}
            hint="Approve or reject them before processing if they should be paid this period. Pending and rejected bills are never added to salary, PF, ESI, or PT."
          />
        ) : null}

        {isSuperAdmin && otherPending.length > 0 ? (
          <section className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4">
            <p className="text-sm font-semibold text-amber-950">Payroll requests waiting for your approval</p>
            <ul className="mt-3 space-y-2">
              {otherPending.map((item) => (
                <li key={item.payroll_run_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 border border-amber-100">
                  <div className="text-sm text-slate-700">
                    <span className="font-semibold text-slate-900">{item.payroll_month || "Payroll"}</span>
                    <span className="text-slate-500"> · requested {fmtDateTime(item.requested_at)} · {item.employee_count} employees</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPayrollId(item.payroll_run_id)}
                    className="rounded-lg bg-amber-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-800"
                  >
                    Review
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {approvalPending || approvalRejected ? (
          <section className={`rounded-xl border px-5 py-4 ${approvalRejected ? "border-rose-200 bg-rose-50/70" : "border-indigo-200 bg-indigo-50/50"}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-indigo-700">
                  {approvalRejected ? "Request rejected" : "Waiting for super admin"}
                </p>
                <h2 className="mt-1 text-base font-semibold text-slate-900">
                  {approval.payroll_month || payPeriodLabel || "Payroll"} approval
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  {approvalRejected
                    ? `${approval.decision_note || "The super admin rejected this request."} Salary was not processed.`
                    : "Salary is not processed until a super admin approves this request."}
                </p>
              </div>
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${approvalRejected ? "bg-rose-100 text-rose-800" : "bg-indigo-100 text-indigo-800"}`}>
                {approvalRejected ? "Rejected" : "Pending"}
              </span>
            </div>
            <ApprovalFacts approval={approval} />
            <p className="mt-3 text-xs text-slate-500">
              Attendance pay is the monthly salary for payable days, before statutory deductions. Gross, net, and statutory deductions are calculated when the super admin processes the payroll.
            </p>
            {isSuperAdmin && approvalPending ? (
              <div className="mt-4 flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmState({
                    title: "Reject this payroll request?",
                    message: "Salary will not be processed. HR can send a new request later.",
                    confirmLabel: "Reject request",
                    danger: true,
                    action: "reject",
                  })}
                  className="rounded-lg border border-rose-300 bg-white px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50"
                >
                  Reject
                </button>
                <button
                  type="button"
                  onClick={askProcess}
                  disabled={payrollBlockedMissingPackages || packageCheckLoading || polling}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  Approve & process
                </button>
              </div>
            ) : null}
          </section>
        ) : null}

        <SectionCard
          step={1}
          title="Payroll ID"
          subtitle="The Payroll ID name below is the same date range from the Payroll ID page."
        >
          {payPeriodLabel ? (
            <div className="pp-run-id">
              <span className="pp-run-id__label">Payroll ID</span>
              <strong className="pp-run-id__name">{payPeriodLabel}</strong>
              {payrollIdNotice?.name ? (
                <span className="pp-id-badge pp-id-badge--created">Created</span>
              ) : null}
            </div>
          ) : null}
          <PayrollRunSelect
            value={payrollId}
            onChange={(id) => setPayrollId(id)}
            reloadToken={`${runSnapshot?.status || ""}:${runSnapshot?.execution_status || ""}:${runSnapshot?.processed_at || ""}`}
            liveStatus={
              runSnapshot && String(runSnapshot.payroll_run_id) === String(payrollId)
                ? { label: liveRunStatus.label, className: liveRunStatus.className }
                : null
            }
            label="Payroll ID"
            required
            emptyLabel="Select Payroll ID"
            selectClassName="w-full border border-slate-300 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            hint={payPeriodLabel || "Choose the Payroll ID to calculate"}
          />
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatTile
              label="Status"
              value={
                <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium ${liveRunStatus.className}`}>
                  {liveRunStatus.label}
                </span>
              }
            />
            <StatTile label="Pay mode" value={payrollMode === "attendance_units" ? "Working days" : "Calendar month"} />
            <StatTile label={processed ? "Processed" : "Created"} value={fmtDateTime(processed ? completedAt : startedAt)} />
          </div>
          {polling && <p className="mt-3 text-sm text-blue-700 font-medium">Processing... updating every 3 seconds.</p>}
        </SectionCard>

        <SectionCard
          step={2}
          title={processed ? "Processed payroll" : "Attendance"}
          subtitle={
            processed
              ? "Gross, deductions, and net pay for this Payroll ID. One page shows 5 employees."
              : "Attendance for this pay period. Salary amounts appear here after payroll is processed."
          }
          actions={
            <>
              {processed ? <DownloadMenu disabled={!employeeRows.length} onPick={onDownload} /> : null}
              <button
                type="button"
                onClick={regenerateForAttendanceMode}
                disabled={regenerateLoading || !payrollId.trim() || polling}
                className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3.5 py-2 text-sm font-semibold text-amber-950 shadow-sm transition hover:border-amber-400 hover:bg-amber-100 disabled:opacity-50"
              >
                <RefreshCw size={15} className={regenerateLoading ? "animate-spin" : ""} />
                {regenerateLoading ? "Recalculating..." : "Refresh & recalculate"}
              </button>
            </>
          }
        >
          {attendanceLoading && !processed ? <p className="text-sm text-slate-500">Loading attendance...</p> : null}
          {detailLoading && processed ? <p className="text-sm text-slate-500">Loading processed amounts...</p> : null}
          {attendanceError && !processed ? (
            <NoticeCard
              variant="error"
              title="Could not load attendance"
              message={attendanceError}
              steps={[
                "Check your connection and refresh this page",
                "If the problem continues, contact your administrator",
              ]}
            />
          ) : null}
          {!payrollId.trim() && !processed ? (
            <p className="text-sm text-slate-600">Select a Payroll ID above to load attendance for that pay period.</p>
          ) : null}
          {payrollId.trim() && !processed && !attendanceLoading && !attendanceError && employeeRows.length === 0 ? (
            <NoticeCard
              variant="warning"
              title="No attendance recorded for this period"
              message="Payroll uses attendance to calculate payable days and prorate salaries. Without records, pay amounts may be wrong or incomplete."
              hint="Open Attendance Calendar and click Fill working days to save Present marks, then return here."
              steps={[
                "Open Attendance Calendar for this pay period month",
                "Click Fill working days to save Present for unmarked working days",
                "Return here — payable days will appear in this step",
                isSuperAdmin ? "Then click Process Payroll to calculate salaries" : "Then send the payroll for super admin approval",
              ]}
              actions={
                <Link
                  to="/attendance/apply-calendar"
                  className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                >
                  Open attendance
                </Link>
              }
            />
          ) : null}

          {processed ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              <StatTile label="Gross pay" value={`₹${formatMoney(runSnapshot?.gross_pay_total)}`} accent="bg-emerald-50 border-emerald-200" />
              <StatTile label="Net pay" value={`₹${formatMoney(runSnapshot?.net_pay_total)}`} accent="bg-teal-50 border-teal-200" />
              <StatTile label="Deductions" value={`₹${formatMoney(runTotals.deductions)}`} accent="bg-red-50 border-red-200" />
              <StatTile label="Employees" value={runSnapshot?.execution_meta?.employees_processed ?? employeeRows.length} hint={fmtDateTime(completedAt)} />
            </div>
          ) : null}

          {employeeRows.length > 0 && !processed ? (
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-xs font-semibold uppercase text-slate-500">
                    <th className="p-3 text-left">Employee</th>
                    <th className="p-3 text-right">Working days</th>
                    <th className="p-3 text-right">Present</th>
                    <th className="p-3 text-right">Holidays</th>
                    <th className="p-3 text-right">Half day</th>
                    <th className="p-3 text-right">Leave</th>
                    <th className="p-3 text-right">Absent</th>
                    <th className="p-3 text-right">LOP days</th>
                    <th className="p-3 text-right">Payable days</th>
                    <th className="p-3 text-right">Monthly amount</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row) => (
                    <tr key={row.employee_id} className="border-t border-slate-100">
                      <td className="p-3 align-top">
                        <div className="font-medium">{row.employee_name}</div>
                        {row.employee_code ? <div className="text-xs text-slate-400">{row.employee_code}</div> : null}
                      </td>
                      <td className="p-3 text-right align-top tabular-nums">{fmtNum(row.total_working_days, 1)}</td>
                      <td className="p-3 text-right align-top tabular-nums">{fmtNum(row.present_units, 1)}</td>
                      <td className={`p-3 text-right align-top tabular-nums ${numOrZero(row.holiday_units) > 0 ? "font-semibold text-violet-700" : "text-slate-400"}`}>
                        {fmtNum(row.holiday_units, 1)}
                      </td>
                      <td className="p-3 text-right align-top tabular-nums">{fmtNum(row.half_day_units, 1)}</td>
                      <td className="p-3 text-right align-top tabular-nums">{fmtNum(row.paid_leave_units, 1)}</td>
                      <td className="p-3 text-right align-top tabular-nums">{fmtNum(row.absent_units, 1)}</td>
                      <td className={`p-3 text-right align-top tabular-nums ${numOrZero(row.lop_units) > 0 ? "font-medium text-red-700" : "text-slate-400"}`}>
                        {fmtNum(row.lop_units, 1)}
                      </td>
                      <td className="p-3 text-right align-top tabular-nums font-semibold text-indigo-700">
                        {fmtNum(row.attendance_payable_days ?? row.payable_days, 1)}
                      </td>
                      <td className="p-3 text-right align-top tabular-nums font-semibold text-slate-900">
                        {payableMonthlyAmount(row) == null ? "—" : `₹${formatMoney(payableMonthlyAmount(row))}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {processed && employeeRows.length > 0 ? (
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-xs font-semibold uppercase text-slate-500">
                    <th className="p-3 text-left">Employee</th>
                    <th className="p-3 text-right">Payable days</th>
                    <th className="p-3 text-right">Monthly amount</th>
                    <th className="p-3 text-right">Gross (₹)</th>
                    <th className="p-3 text-right">Deductions (₹)</th>
                    <th className="p-3 text-right">Net pay (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row) => (
                    <tr key={row.employee_id} className="border-t border-slate-100">
                      <td className="p-3">
                        <div className="font-medium">{row.employee_name}</div>
                        {row.employee_code ? <div className="text-xs text-slate-400">{row.employee_code}</div> : null}
                      </td>
                      <td className="p-3 text-right tabular-nums">{fmtNum(row.payable_days, 1)}</td>
                      <td className="p-3 text-right tabular-nums">
                        {payableMonthlyAmount(row) == null ? "—" : `₹${formatMoney(payableMonthlyAmount(row))}`}
                      </td>
                      <td className="p-3 text-right tabular-nums">₹{formatMoney(row.earnings)}</td>
                      <td className="p-3 text-right tabular-nums text-red-700">₹{formatMoney(row.deductions)}</td>
                      <td className="p-3 text-right tabular-nums font-semibold text-emerald-800">₹{formatMoney(row.net_salary)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 font-semibold border-t-2">
                  <tr>
                    <td className="p-3">Total</td>
                    <td colSpan={2} />
                    <td className="p-3 text-right">₹{formatMoney(runTotals.earnings ?? runSnapshot?.gross_pay_total)}</td>
                    <td className="p-3 text-right text-red-700">₹{formatMoney(runTotals.deductions)}</td>
                    <td className="p-3 text-right text-emerald-800">₹{formatMoney(runTotals.net ?? runSnapshot?.net_pay_total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : null}
          <TablePager page={safePage} pageCount={pageCount} total={employeeRows.length} onPage={setTablePage} />
        </SectionCard>

        {!processed && !isSuperAdmin ? (
          <section className="rounded-xl border border-indigo-200 bg-white px-5 py-4">
            <p className="text-xs font-bold uppercase tracking-wider text-indigo-700">Send for approval</p>
            <h2 className="mt-1 text-base font-semibold text-slate-900">{periodName}</h2>
            <p className="mt-1 text-sm text-slate-600">
              This does not process salary. The super admin sees this request on Approvals, under Payroll.
            </p>
            <dl className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div className="rounded-lg bg-slate-50 px-3 py-2">
                <dt className="text-slate-500">Employees</dt>
                <dd className="font-semibold text-slate-900 tabular-nums">{employeeRows.length}</dd>
              </div>
              <div className="rounded-lg bg-slate-50 px-3 py-2">
                <dt className="text-slate-500">Attendance pay</dt>
                <dd className="font-semibold text-slate-900 tabular-nums">{moneyOrDash(attendancePay)}</dd>
              </div>
              <div className="rounded-lg bg-slate-50 px-3 py-2">
                <dt className="text-slate-500">Status</dt>
                <dd className="font-semibold text-slate-900">{approvalPending ? "Waiting for super admin" : "Not sent"}</dd>
              </div>
            </dl>
            {employeeRows.length === 0 ? (
              <p className="mt-3 text-sm text-amber-800">
                Attendance for this Payroll ID is not on the page yet. Load it above before sending, so the request shows the right employee count and attendance pay.
              </p>
            ) : null}
          </section>
        ) : null}

        {!processed && !(isSuperAdmin && approvalPending) ? (
        <div className="sticky bottom-0 z-10 -mx-2 px-2 py-3 bg-gray-50/95 backdrop-blur border-t border-slate-200 rounded-t-xl">
          <div className="flex flex-wrap gap-2 justify-end items-center">
            {!isSuperAdmin && !processed ? (
              <button
                type="button"
                onClick={askApprovalRequest}
                disabled={approvalPending || payrollBlockedMissingPackages || packageCheckLoading || polling || requestBusy}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-lg font-semibold disabled:opacity-50"
              >
                {approvalPending ? "Waiting for approval" : "Request approval"}
              </button>
            ) : null}
            {isSuperAdmin && !approvalPending ? (
              <button
                type="button"
                onClick={askProcess}
                disabled={processed || payrollBlockedMissingPackages || packageCheckLoading || polling}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-lg font-semibold disabled:opacity-50"
              >
                {polling ? "Processing..." : processed ? "Payroll processed" : "Process Payroll"}
              </button>
            ) : null}
          </div>
        </div>
        ) : null}

        <ConfirmDialog
          open={Boolean(confirmState)}
          title={confirmState?.title}
          message={confirmState?.message}
          confirmLabel={confirmState?.confirmLabel}
          danger={confirmState?.danger}
          busy={requestBusy || polling}
          onCancel={() => setConfirmState(null)}
          onConfirm={() => {
            if (confirmState?.action === "request") submitApprovalRequest();
            else if (confirmState?.action === "reject") rejectApproval();
            else runPayroll();
          }}
        >
          {confirmState?.action === "request" ? (
            <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
              <p>{periodName} · {employeeRows.length} employees</p>
              <p className="mt-1">Attendance pay {moneyOrDash(attendancePay)}</p>
            </div>
          ) : null}
        </ConfirmDialog>
      </div>
    </div>
  );
}
